// static/js/history.js
import axios from 'axios';

/**
 * Fetches conversations from the API and populates the history list.
 */
async function loadConversations() {
    const historyList = document.getElementById('history-list');
    if (!historyList) {
        console.error("History list element not found.");
        return;
    }

    historyList.innerHTML = '<li class="nav-item"><span class="nav-link text-muted">Loading...</span></li>';

    try {
        const response = await axios.get('/api/conversations');
        const conversations = response.data;

        historyList.innerHTML = ''; // Clear loading indicator

        if (!conversations || conversations.length === 0) {
            historyList.innerHTML = '<li class="nav-item"><span class="nav-link text-muted">No past conversations found.</span></li>';
            return;
        }

        conversations.forEach(convo => {
            const li = document.createElement('li');
            li.className = 'nav-item';

            // Robustly get the conversation ID
            let conversationId;
            if (typeof convo._id === 'object' && convo._id !== null && convo._id.$oid) {
                conversationId = convo._id.$oid;
            } else {
                conversationId = convo._id;
            }
            
            if (!conversationId) {
                console.error("Could not determine conversation ID for:", convo);
                return; // Skip this item
            }

            li.dataset.id = conversationId;

            // Highlight the active conversation
            const currentConversationId = new URLSearchParams(window.location.search).get('conversation_id');
            if (currentConversationId === conversationId) {
                li.classList.add('active');
            }
            
            // The title is user- or LLM-written, so it only ever reaches the
            // DOM through textContent and property assignment, never through
            // interpolated markup: a quote in it must not end an attribute.
            const link = document.createElement('a');
            link.className = 'nav-link history-link';
            link.href = `/chat?conversation_id=${encodeURIComponent(conversationId)}`;
            link.title = convo.title;

            const titleSpan = document.createElement('span');
            titleSpan.className = 'history-title';
            titleSpan.textContent = convo.title;
            link.appendChild(titleSpan);

            li.appendChild(link);
            li.insertAdjacentHTML('beforeend', `
                <span class="history-icons">
                    <i class="fas fa-download download-icon" title="Download conversation"></i>
                    <i class="fas fa-pencil-alt rename-icon" title="Rename conversation"></i>
                    <i class="fas fa-trash delete-icon" title="Delete conversation"></i>
                </span>
            `);
            historyList.appendChild(li);
        });

    } catch (error) {
        console.error('Error loading conversation history:', error);
        historyList.innerHTML = '<li class="nav-item"><span class="nav-link text-danger">Error loading history.</span></li>';
    }
}

/**
 * Helper to close the history offcanvas on mobile if it exists.
 */
function closeHistoryOffcanvas() {
    const sidebar = document.getElementById('appSidebar');
    if (sidebar && typeof bootstrap !== 'undefined') {
        const offcanvas = bootstrap.Offcanvas.getInstance(sidebar);
        if (offcanvas) {
            offcanvas.hide();
        }
    }
}

/**
 * Initializes the conversation history feature.
 */
export function initHistory() {
    const historyList = document.getElementById('history-list');
    if (historyList) {
        console.log("History module initialized.");
        loadConversations();

        const newChatSidebarBtn = document.getElementById('new-chat-btn');
        if (newChatSidebarBtn) {
            newChatSidebarBtn.addEventListener('click', () => {
                closeHistoryOffcanvas();
                window.location.href = '/chat';
            });
        }

        // Event listener for deleting conversations
        historyList.addEventListener('click', async (e) => {
            if (e.target.classList.contains('delete-icon')) {
                const listItem = e.target.closest('.nav-item');
                const conversationId = listItem.dataset.id;

                if (confirm('Are you sure you want to delete this conversation?')) {
                    try {
                        const response = await axios.delete(`/api/conversations/${conversationId}`);
                        if (response.status === 204) {
                            const isActive = new URLSearchParams(window.location.search).get('conversation_id') === conversationId;
                            
                            listItem.remove();

                            if (isActive) {
                                window.location.href = '/chat';
                            }
                            
                            // If list is now empty, show the message
                            if (historyList.children.length === 0) {
                                historyList.innerHTML = '<li class="nav-item"><span class="nav-link text-muted">No past conversations found.</span></li>';
                            }
                        }
                    } catch (error) {
                        console.error('Failed to delete conversation:', error);
                        alert('Failed to delete conversation.');
                    }
                }
            }
            // Event listener for renaming conversations
            if (e.target.classList.contains('rename-icon')) {
                const listItem = e.target.closest('.nav-item');
                const conversationId = listItem.dataset.id;
                const historyLink = listItem.querySelector('.history-link');
                const historyTitleSpan = listItem.querySelector('.history-title');
                const originalTitle = historyTitleSpan.textContent;

                // Hide the link and show an input field
                historyLink.style.display = 'none';
                // The icons overlay the row's right edge; keep them off the input.
                listItem.classList.add('is-renaming');

                const input = document.createElement('input');
                input.type = 'text';
                input.className = 'form-control form-control-sm'; // Bootstrap styling
                input.value = originalTitle;
                listItem.insertBefore(input, historyLink.nextSibling); // Insert after historyLink
                input.focus();

                const saveRename = async () => {
                    const newTitle = input.value.trim();
                    if (newTitle && newTitle !== originalTitle) {
                        try {
                            await axios.put(`/api/conversations/${conversationId}`, { title: newTitle });
                            historyTitleSpan.textContent = newTitle;
                            historyLink.title = newTitle; // Update tooltip
                        } catch (error) {
                            console.error('Failed to rename conversation:', error);
                            alert('Failed to rename conversation.');
                            historyTitleSpan.textContent = originalTitle; // Revert on error
                            historyLink.title = originalTitle;
                        }
                    }
                    // Clean up: remove input and show link
                    input.remove();
                    historyLink.style.display = ''; // Show again
                    listItem.classList.remove('is-renaming');
                };

                input.addEventListener('blur', saveRename);
                input.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter') {
                        input.removeEventListener('blur', saveRename); // Prevent blur from firing twice
                        saveRename();
                    } else if (e.key === 'Escape') {
                        input.removeEventListener('blur', saveRename); // Prevent blur from firing twice
                        input.remove();
                        historyLink.style.display = ''; // Show again
                        listItem.classList.remove('is-renaming');
                    }
                });
            }
            // Event listener for downloading conversations
            if (e.target.classList.contains('download-icon')) {
                const listItem = e.target.closest('.nav-item');
                const conversationId = listItem.dataset.id;
                window.location.href = `/api/conversations/${conversationId}/download`;
            }
        });

        document.addEventListener('conversationcreated', () => {
            console.log('New conversation created, refreshing history list...');
            loadConversations();
        });

        // Event listener for searching history
        const historySearch = document.getElementById('history-search');
        if (historySearch) {
            historySearch.addEventListener('input', (e) => {
                const searchTerm = e.target.value.toLowerCase();
                const items = historyList.querySelectorAll('.nav-item');

                items.forEach(item => {
                    const titleElement = item.querySelector('.history-title');
                    if (titleElement) {
                        const title = titleElement.textContent.toLowerCase();
                        if (title.includes(searchTerm)) {
                            item.style.display = '';
                        } else {
                            item.style.display = 'none';
                        }
                    }
                });
            });
        }
    }
}

